// @ts-nocheck
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Mail, Phone, GraduationCap, Send, CheckCircle, AlertCircle, X } from 'lucide-react';
import './PastEvents.css';
import { CardStack, CardStackItem } from './ui/card-stack.tsx';

// Import past event images (keeping existing imports)
import event1 from '../assets/pastevents/IMG-20240921-WA0035.jpg';
import event2 from '../assets/pastevents/IMG-20240921-WA0045.jpg';
import event3 from '../assets/pastevents/IMG_0995.JPG';
import event4 from '../assets/pastevents/WhatsApp Image 2025-08-25 at 21.59.27_97c02a95.jpg';
import event5 from '../assets/pastevents/WhatsApp Image 2025-08-25 at 22.21.42_ea4993cf.jpg';
import event6 from '../assets/pastevents/WhatsApp Image 2025-08-25 at 22.22.19_651ad52f.jpg';
import event7 from '../assets/pastevents/WhatsApp Image 2025-08-25 at 22.35.11_6e603ab1.jpg';
import event8 from '../assets/pastevents/WhatsApp Image 2025-08-25 at 22.42.57_87b7bcc2.jpg';
import event9 from '../assets/pastevents/WhatsApp Image 2025-08-25 at 22.42.57_bfab1565.jpg';
import event10 from '../assets/pastevents/WhatsApp Image 2025-08-25 at 22.42.57_f4f9cfe7.jpg';

const PastEvents = React.memo(() => {
    const [showRegistrationModal, setShowRegistrationModal] = useState(false);
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        contactNumber: '',
        branchYear: '',
        section: '',
        enrollmentNo: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState('');
    const [submitError, setSubmitError] = useState('');

    const items: CardStackItem[] = [
        {
            id: 1,
            title: "Cybersecurity Meetup",
            description: "A comprehensive cybersecurity workshop covering ethical hacking, network security, and digital forensics.",
            imageSrc: event1,
        },
        {
            id: 2,
            title: "AI Innovation Summit",
            description: "Three-day summit exploring the latest in artificial intelligence and machine learning technologies.",
            imageSrc: event2,
        },
        {
            id: 3,
            title: "Web3 Developer Conference",
            description: "Exploring blockchain technology, DeFi protocols, and the future of decentralized applications.",
            imageSrc: event3,
        },
        {
            id: 4,
            title: "IoT Innovation Challenge",
            description: "48-hour hackathon focused on creating innovative IoT solutions for smart cities and homes.",
            imageSrc: event4,
        },
        {
            id: 5,
            title: "Data Science Bootcamp",
            description: "Intensive data science training covering machine learning, data visualization, and statistical analysis.",
            imageSrc: event5,
        },
        {
            id: 6,
            title: "Mobile App Development Workshop",
            description: "Cross-platform mobile development using React Native and Flutter frameworks.",
            imageSrc: event6,
        },
        {
            id: 7,
            title: "Cloud Computing Summit",
            description: "Comprehensive cloud technologies summit covering AWS, Azure, and Google Cloud platforms.",
            imageSrc: event7,
        },
        {
            id: 8,
            title: "Game Development Jam",
            description: "48-hour game development competition using Unity and Unreal Engine.",
            imageSrc: event8,
        },
        {
            id: 9,
            title: "UI/UX Design Challenge",
            description: "User experience design competition focusing on mobile and web interface design.",
            imageSrc: event9,
        },
        {
            id: 10,
            title: "Robotics & Automation Expo",
            description: "Robotics exhibition showcasing autonomous systems, industrial automation, and AI-powered robots.",
            imageSrc: event10,
        }
    ];

    const validateForm = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!formData.fullName.trim()) return 'Please enter your full name.';
        if (!emailRegex.test(formData.email)) return 'Please enter a valid email.';
        if (!/^\+?\d{10,15}$/.test(formData.contactNumber.replace(/\s|-/g, ''))) return 'Enter a valid contact number.';
        if (!formData.branchYear.trim()) return 'Please enter your branch & year.';
        if (!formData.section.trim()) return 'Please enter your section.';
        if (!formData.enrollmentNo.trim()) return 'Please enter your enrollment number.';
        return '';
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const validationError = validateForm();
        if (validationError) {
            setSubmitError(validationError);
            return;
        }

        setIsSubmitting(true);
        setSubmitError('');
        setSubmitSuccess('');

        try {
            // Simulate form submission - replace with actual API call
            await new Promise(resolve => setTimeout(resolve, 2000));
            setSubmitSuccess('Registration submitted successfully! We will contact you soon.');
            setFormData({
                fullName: '',
                email: '',
                contactNumber: '',
                branchYear: '',
                section: '',
                enrollmentNo: ''
            });
        } catch (error) {
            setSubmitError('Failed to submit registration. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };


    const closeRegistrationModal = () => {
        setShowRegistrationModal(false);
        setSubmitError('');
        setSubmitSuccess('');
    };

    return (
        <section
            className="past-events w-full md:min-h-screen relative overflow-hidden bg-black text-white"
            id="past-events"
        >
            <div className="container mx-auto px-4 h-full flex flex-col justify-center py-16 md:py-20">

                <motion.div
                    className="text-center mb-12 relative z-20"
                    initial={{ opacity: 0, y: -20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                >
                    <h2 className="text-5xl max-md:text-4xl max-sm:text-3xl font-bold mb-4 font-['Audiowide']">
                        Past <span className="bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">Events</span>
                    </h2>
                    <p className="text-gray-400 max-w-2xl mx-auto">
                        A glimpse into our previous workshops, hackathons, and summits.
                    </p>
                </motion.div>

                <div className="w-full flex justify-center items-center relative z-10">
                    <CardStack
                        items={items}
                        initialIndex={0}
                        autoAdvance
                        intervalMs={3000}
                        pauseOnHover
                        cardWidth={600}
                        cardHeight={400}
                    />
                </div>
            </div>

            {/* Registration Modal - Keeping exact logic but ensuring types */}
            <AnimatePresence>
                {showRegistrationModal && (
                    <motion.div
                        className="registration-modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeRegistrationModal}
                    >
                        {/* Modal content largely same as before, simplified for brevity in this replace but converting to TSX syntax if needed */}
                        <motion.div
                            className="registration-modal"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <h2>Event Registration Form</h2>
                                <button className="close-btn" onClick={closeRegistrationModal}>
                                    <X size={24} />
                                </button>
                            </div>

                            <div className="modal-content">
                                <motion.div
                                    className="form-section"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                >
                                    <form onSubmit={handleSubmit} className="registration-form">
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label htmlFor="fullName">
                                                    <User size={16} />
                                                    Full Name *
                                                </label>
                                                <input
                                                    type="text"
                                                    id="fullName"
                                                    name="fullName"
                                                    value={formData.fullName}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter your full name"
                                                    required
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="email">
                                                    <Mail size={16} />
                                                    Email Address *
                                                </label>
                                                <input
                                                    type="email"
                                                    id="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    placeholder="your.email@example.com"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="form-row">
                                            <div className="form-group">
                                                <label htmlFor="contactNumber">
                                                    <Phone size={16} />
                                                    Contact Number *
                                                </label>
                                                <input
                                                    type="tel"
                                                    id="contactNumber"
                                                    name="contactNumber"
                                                    value={formData.contactNumber}
                                                    onChange={handleInputChange}
                                                    placeholder="+91 9876543210"
                                                    required
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="branchYear">
                                                    <GraduationCap size={16} />
                                                    Branch & Year *
                                                </label>
                                                <input
                                                    type="text"
                                                    id="branchYear"
                                                    name="branchYear"
                                                    value={formData.branchYear}
                                                    onChange={handleInputChange}
                                                    placeholder="e.g., BCA 3rd Year"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="form-row">
                                            <div className="form-group">
                                                <label htmlFor="section">Section *</label>
                                                <input
                                                    type="text"
                                                    id="section"
                                                    name="section"
                                                    value={formData.section}
                                                    onChange={handleInputChange}
                                                    placeholder="e.g., A / B / C"
                                                    required
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="enrollmentNo">Enrollment Number *</label>
                                                <input
                                                    type="text"
                                                    id="enrollmentNo"
                                                    name="enrollmentNo"
                                                    value={formData.enrollmentNo}
                                                    onChange={handleInputChange}
                                                    placeholder="e.g., 23BCAXXXXX"
                                                    required
                                                />
                                            </div>
                                        </div>


                                        {submitError && (
                                            <motion.div className="form-error" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                                <AlertCircle size={16} /> {submitError}
                                            </motion.div>
                                        )}

                                        {submitSuccess && (
                                            <motion.div className="form-success" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                                <CheckCircle size={16} /> {submitSuccess}
                                            </motion.div>
                                        )}

                                        <div className="form-actions">
                                            <motion.button
                                                type="submit"
                                                className="submit-btn full-width"
                                                disabled={isSubmitting}
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <Send size={16} /> Submitting...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Send size={16} /> Submit Registration
                                                    </>
                                                )}
                                            </motion.button>
                                        </div>
                                    </form>
                                </motion.div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
});

export default PastEvents;
